import './styles.css';
import { CinematicComposer } from './render/composer';
import { AudioEngine } from './audio/engine';
import { Hud } from './ui/hud';
import { MapOverlay } from './ui/map';
import { Director } from './app/director';
import { newRun } from './app/state';
import { TRACKS } from './data/music';
import { DEDICATION } from './data/letter';
import { SunChapter } from './chapters/sun';
import { GalaxyChapter } from './chapters/galaxy';
import { S2Chapter } from './chapters/s2';
import { LensChapter } from './chapters/lens';
import { PitChapter } from './chapters/pit';

function boot() {
  const stage = document.getElementById('stage') as HTMLElement;
  const canvas = document.getElementById('gl') as HTMLCanvasElement;
  const labels = document.getElementById('labels') as HTMLElement;
  const hudRoot = document.getElementById('hud') as HTMLElement;
  const overlay = document.getElementById('overlay') as HTMLElement;
  if (!document.createElement('canvas').getContext('webgl2')) {
    overlay.innerHTML = '<div class="cardWrap"><div class="card"><h2>WebGL 2 is needed</h2><p>This letter renders a galaxy and a black hole live. Your browser has WebGL 2 turned off or unavailable.</p></div></div>';
    return;
  }

  const composer = new CinematicComposer(canvas);
  const audio = new AudioEngine();
  audio.setPlaylist(TRACKS);
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  let cinematic = window.innerWidth >= 760 && !coarse;
  composer.setQuality(cinematic);

  const pit = new PitChapter();
  pit.setQuality(cinematic);
  const chapters = [new SunChapter(), new GalaxyChapter(), new S2Chapter(), new LensChapter(), pit];

  const map = new MapOverlay(stage);
  stage.insertBefore(map.el, labels);   // above the picture, below the labels and the cards

  let run = newRun();
  let director: Director;
  const soundOn = () => { if (!audio.started) { audio.start(); hud.setSound(true); } };
  const hud = new Hud(hudRoot, labels, {
    onSound: () => { if (!audio.started) soundOn(); else { audio.setMuted(!audio.isMuted); hud.setSound(!audio.isMuted); } },
    onQuality: () => { cinematic = !cinematic; composer.setQuality(cinematic); pit.setQuality(cinematic); hud.setQuality(cinematic); run.quality = cinematic ? 'cinematic' : 'fast'; },
    onPause: () => director.togglePause(),
    onPrev: () => { soundOn(); audio.prev(); },
    onPlayPause: () => { soundOn(); audio.togglePause(); },
    onNext: () => { soundOn(); audio.next(); },
    onVolume: (v) => audio.setMusicVolume(v),
    onChapter: (i) => director.jump(i),
    onChapterBack: () => director.back(),
    onChapterNext: () => director.next(),
    onDemo: () => director.demonstrate(),
    onTapRight: () => director.advance(),
    onTapLeft: () => director.retreat(),
    onSkip: () => director.skip()
  });
  hud.setVoices({ lover: DEDICATION.from || 'Me', expert: 'The guide' });
  hud.setQuality(cinematic);
  hud.setSound(false);
  hud.setPlayerVisible(audio.hasMusic);
  const byLine = (title: string) => { const t = TRACKS.find((x) => x.title === title); return t ? `${t.title}, by ${t.artist}` : title; };
  audio.onTrack = (title, playing) => hud.setTrack(byLine(title), playing);
  hud.setTrack(byLine(TRACKS[0]?.title ?? ''), false);

  const makeDirector = () => new Director({ chapters, composer, audio, hud, map, overlay, canvas, run, onRestart: () => { run = newRun(); run.quality = cinematic ? 'cinematic' : 'fast'; audio.reset(); director = makeDirector(); director.start(); } });
  director = makeDirector();
  director.start();
  if (import.meta.env.DEV) (window as any).__voyage = { get director() { return director; }, audio, composer, chapters, hud };

  // input: one finger drags, two fingers pinch to approach, a tap without movement is a tap, wheel approaches,
  // keys step the words and drive the levers. Drags and pinches reach the chapter at any time.
  const touches = new Map<number, { x: number; y: number }>();
  let dragging = false, lx = 0, ly = 0, moved = 0, pinchDist = 0;
  const pinchDistance = () => { const [a, b] = [...touches.values()]; return Math.hypot(a.x - b.x, a.y - b.y); };
  canvas.addEventListener('pointerdown', (e) => {
    touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    canvas.setPointerCapture(e.pointerId);
    if (touches.size === 2) { pinchDist = pinchDistance(); moved = 1e9; dragging = false; return; }   // a second finger: a pinch, never a tap
    dragging = true; moved = 0; lx = e.clientX; ly = e.clientY; canvas.classList.add('dragging');
  });
  canvas.addEventListener('pointermove', (e) => {
    if (touches.has(e.pointerId)) touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (touches.size >= 2) { const d = pinchDistance(); if (pinchDist > 0) director.onWheel((pinchDist - d) * 2.5); pinchDist = d; return; }
    if (!dragging) return;
    moved += Math.abs(e.clientX - lx) + Math.abs(e.clientY - ly); director.onDrag(e.clientX - lx, e.clientY - ly); lx = e.clientX; ly = e.clientY;
  });
  const up = (e: PointerEvent) => {
    touches.delete(e.pointerId);
    if (dragging && moved < 6) { const r = canvas.getBoundingClientRect(); director.onClick(e.clientX - r.left, e.clientY - r.top); }
    dragging = false; pinchDist = 0; canvas.classList.remove('dragging');
  };
  canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', (e) => { touches.delete(e.pointerId); dragging = false; pinchDist = 0; canvas.classList.remove('dragging'); });
  canvas.addEventListener('wheel', (e) => { director.onWheel(e.deltaY); e.preventDefault(); }, { passive: false });
  map.el.addEventListener('click', () => director.onward());
  document.addEventListener('keydown', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    if (t && t.tagName === 'BUTTON' && (e.key === 'Enter' || e.key === ' ')) return;   // the button handles it
    if (director.onKey(e.key)) e.preventDefault();
  });

  let last = 0;
  const frame = (now: number) => {
    requestAnimationFrame(frame);
    const t = now / 1000, dt = last ? Math.min(1.0, t - last) : 0; last = t;
    const info = composer.beginFrame(dt, t);
    director.update(dt, t, info.width, info.height);
    if (director.renderScene) { composer.render(); director.afterRender(); }
  };
  requestAnimationFrame(frame);
}

boot();
